import type { Layer, Path, PathOptions } from "leaflet";
import type { GenericLayerStyle } from "../model/genericLayer.ts";
import type { SubBasin } from "../model/types.ts";
import { BASE_STYLE, PROBLEM_STYLE, SELECTION_FILL_COLOR, UNLINKED_STYLE } from "./mapStyles.ts";
import { patternKeyFor } from "../model/assignmentColors.ts";

export const SELECTION_HIGHLIGHT = { color: SELECTION_FILL_COLOR, weight: 3 };

export function isPath(layer: Layer): layer is Path {
  return "setStyle" in layer;
}

type VisualState = "normal" | "problem" | "unlinked";

export function visualStateOf(
  subBasin: SubBasin | null,
  problemsOf: (c: SubBasin) => string[]
): VisualState {
  if (!subBasin) return "unlinked";
  return problemsOf(subBasin).length > 0 ? "problem" : "normal";
}

/**
 * `withSnow` swaps the solid fill for a diagonal stripe SVG pattern over the
 * same base color (see `<SnowPatterns>`, SnowPatterns.tsx) — the color
 * itself doesn't change, so "has a snow zone" doesn't compete with the
 * color coding already used by "problem"/selection/assignment mode. Always
 * set as a plain `fillColor` (a `url(#pattern-...)` reference), NEVER as a
 * CSS `className` — `selected` (styleFor below) needs to swap this same
 * fillColor again on top, via `layer.setStyle(...)` alone, without
 * remounting the `<GeoJSON>` layer (see onEachFeature.ts); Leaflet's SVG
 * renderer only applies `options.className` once, at mount (`_initPath`),
 * `_updateStyle` (what `setStyle` calls) never touches it — so a class
 * picked at mount would keep permanently winning over every later
 * `fillColor` this function or `styleFor` tries to set, selected or not.
 * Only the normal/problem variants are needed: an unlinked sub-basin
 * (`subBasin === null`) can never have `snowZoneCode`, so that state never
 * combines with snow.
 */
function baseStyleFor(state: VisualState, withSnow: boolean, colorFill?: string) {
  const base =
    state === "problem" ? PROBLEM_STYLE : state === "unlinked" ? UNLINKED_STYLE : BASE_STYLE;
  if (colorFill) {
    // Own colored stripe pattern (SnowPatterns' pattern-assign-N) when
    // there's also a snow zone, so it doesn't lose "this sub-basin has
    // snow" entirely by just overwriting the fill with a plain color.
    return {
      ...base,
      fillColor: withSnow ? `url(#pattern-assign-${patternKeyFor(colorFill)})` : colorFill,
    };
  }
  if (!withSnow) return base;
  return { ...base, fillColor: `url(#pattern-snow-${state === "problem" ? "issue" : "normal"})` };
}

/**
 * `colorFill`, when given, overrides the fill color (assignmentColors.ts —
 * color-by-assignment while bulk-editing zones/outlet points/aquifers) but
 * leaves `color`/`weight`/`dashArray` from `state` alone, so a problem/
 * unlinked sub-basin's border still reads as such even while its fill is
 * showing which entity it's assigned to instead of its normal/problem
 * color. `fillOpacity` is bumped up a bit — the entity colors need to read
 * clearly, unlike the subtler normal-state tint.
 *
 * `selected` always wins the WHOLE surface, not just the border — every
 * other fill source above (normal/problem/unlinked, snow stripe,
 * assignment color) gets replaced by the selection color/pattern, in every
 * circumstance a sub-basin can be selected in. `withSnow` still needs its
 * own selected pattern (`pattern-selected`, SnowPatterns.tsx) rather than a
 * plain `fillColor`, for the exact same "className never updates
 * dynamically" reason `baseStyleFor` avoids className above — a fixed
 * fillColor would otherwise depend on `state`'s prior className having
 * never been set at all, which is no longer true once selection has its
 * own snow-safe pattern to swap to.
 */
export function styleFor(
  state: VisualState,
  selected: boolean,
  withSnow: boolean,
  colorFill?: string
) {
  const base = baseStyleFor(state, withSnow, colorFill);
  const withColor = colorFill ? { ...base, fillOpacity: Math.max(base.fillOpacity, 0.6) } : base;
  if (!selected) return withColor;
  return {
    ...withColor,
    ...SELECTION_HIGHLIGHT,
    fillColor: withSnow ? "url(#pattern-selected)" : SELECTION_FILL_COLOR,
    fillOpacity: Math.max(withColor.fillOpacity, 0.6),
  };
}

export function hoverStyleFor(
  state: VisualState,
  selected: boolean,
  withSnow: boolean,
  colorFill?: string
) {
  const base = styleFor(state, selected, withSnow, colorFill);
  return { ...base, weight: selected ? 3 : 2, fillOpacity: base.fillOpacity + 0.2 };
}

/**
 * `radius` is a `CircleMarker`-only option, not part of plain `PathOptions`
 * — harmless to always include anyway (Leaflet ignores unknown options on
 * a line/polygon `Path`), so one style object works for `pointToLayer`,
 * the initial `style` prop AND the imperative `setStyle` sync below,
 * regardless of `geometryKind`.
 */
export function genericLayerPathOptions(style: GenericLayerStyle): PathOptions & {
  radius: number;
} {
  return {
    color: style.color,
    weight: style.weight,
    fillColor: style.color,
    fillOpacity: style.fillOpacity,
    radius: style.radius,
  };
}
